import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { ChronologyEvent } from "~/api/model";
import { useChronologyStore } from "../stores/useChronologyStore";
import type { ChronologyDuplicateRowInfo } from "./ChronologyDuplicateDot";
import { SortableChronologyItem } from "./SortableChronologyItem";

/** Extra items rendered beyond the visible viewport edges. */
const OVERSCAN_COUNT = 10;

/** Fallback height (px) used before an item is measured by the virtualizer. */
const ESTIMATED_ITEM_HEIGHT = 100;

type VirtualListItem =
  | { type: "event"; event: ChronologyEvent }
  | { type: "undated-header" };

interface ChronologyListProps {
  events: ChronologyEvent[];
  onReorder: (activeId: string, overId: string) => void;
  /** Duplicate-marker data per event id; rows absent from the map show no dot. */
  duplicateInfoById: Map<string, ChronologyDuplicateRowInfo>;
  /** Opens the review modal scoped to the clicked row's group. */
  onReviewDuplicates: (eventIds: string[]) => void;
}

export const ChronologyList: FC<ChronologyListProps> = ({
  events,
  onReorder,
  duplicateInfoById,
  onReviewDuplicates,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const datedEvents = useMemo(
    () => events.filter((event) => event.eventDate),
    [events]
  );
  const undatedEvents = useMemo(
    () => events.filter((event) => !event.eventDate),
    [events]
  );

  const flatItems = useMemo<VirtualListItem[]>(() => {
    const items: VirtualListItem[] = datedEvents.map((event) => ({
      type: "event" as const,
      event,
    }));
    if (undatedEvents.length > 0) {
      items.push({ type: "undated-header" as const });
      for (const event of undatedEvents) {
        items.push({ type: "event" as const, event });
      }
    }
    return items;
  }, [datedEvents, undatedEvents]);

  const datedEventIds = useMemo(
    () => datedEvents.map((e) => e.id),
    [datedEvents]
  );
  const undatedEventIds = useMemo(
    () => undatedEvents.map((e) => e.id),
    [undatedEvents]
  );

  const getItemKey = useCallback(
    (index: number) => {
      const item = flatItems[index];
      return item.type === "event" ? item.event.id : "undated-header";
    },
    [flatItems]
  );

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
    overscan: OVERSCAN_COUNT,
    getItemKey,
  });

  const highlightedEventId = useChronologyStore(
    (state) => state.highlightedEventId
  );

  useEffect(() => {
    if (!highlightedEventId) return;
    const idx = flatItems.findIndex(
      (item) => item.type === "event" && item.event.id === highlightedEventId
    );
    if (idx >= 0) {
      virtualizer.scrollToIndex(idx, { align: "center" });
    }
  }, [highlightedEventId, flatItems, virtualizer]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const virtualItems = virtualizer.getVirtualItems();

  const datedCount = datedEvents.length;
  const hasUndated = undatedEvents.length > 0;
  const undatedHeaderIndex = hasUndated ? datedCount : -1;

  const virtualDatedItems = virtualItems.filter((vi) => vi.index < datedCount);
  const virtualHeaderItems = virtualItems.filter(
    (vi) => vi.index === undatedHeaderIndex
  );
  const virtualUndatedItems = virtualItems.filter(
    (vi) => hasUndated && vi.index > undatedHeaderIndex
  );

  const datedSectionHeight =
    hasUndated && virtualizer.measurementsCache.length > undatedHeaderIndex
      ? virtualizer.measurementsCache[undatedHeaderIndex].start
      : virtualizer.getTotalSize();

  const renderVirtualEvent = (virtualItem: (typeof virtualItems)[number]) => {
    const item = flatItems[virtualItem.index];
    if (item.type !== "event") return null;
    return (
      <div
        key={item.event.id}
        data-index={virtualItem.index}
        ref={virtualizer.measureElement}
        className="absolute left-0 top-0 w-full pb-[18px]"
        style={{ transform: `translateY(${virtualItem.start}px)` }}
      >
        <SortableChronologyItem
          event={item.event}
          duplicateInfo={duplicateInfoById.get(item.event.id)}
          onReviewDuplicates={onReviewDuplicates}
        />
      </div>
    );
  };

  return (
    <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div
            className="relative"
            style={{ height: virtualizer.getTotalSize() }}
          >
            <div
              className="absolute left-0 top-0 w-0 border-l-2 border-border"
              style={{ height: datedSectionHeight }}
            />
            <SortableContext
              items={datedEventIds}
              strategy={verticalListSortingStrategy}
            >
              {virtualDatedItems.map(renderVirtualEvent)}
            </SortableContext>

            {virtualHeaderItems.map((virtualItem) => (
              <div
                key="undated-header"
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <div className="pb-[18px] pt-4">
                  <p className="mb-1 pl-5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <FormattedMessage
                      id="chronology.list.undatedHeading"
                      defaultMessage="Undated"
                    />
                  </p>
                  <p className="pl-5 text-[11px] leading-tight text-muted-foreground">
                    <FormattedMessage
                      id="chronology.list.undatedDescription"
                      defaultMessage="No date on file — not placed on the chronology"
                    />
                  </p>
                </div>
              </div>
            ))}

            <SortableContext
              items={undatedEventIds}
              strategy={verticalListSortingStrategy}
            >
              {virtualUndatedItems.map(renderVirtualEvent)}
            </SortableContext>
          </div>
        </DndContext>
      </div>
    </div>
  );
};
